import * as DialogPrimitive from '@radix-ui/react-dialog';
import { popupPressedJustNow } from '@/components/ToastHost';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

interface DialogContentProps {
  children: ReactNode;
  title?: string;
  description?: string;
  className?: string;
  /** When `side` is set, renders as a slide-in panel instead of a centered modal. */
  side?: 'right';
  hideCloseButton?: boolean;
  onPointerDownOutside?: (e: Event) => void;
}

const isInToastHost = (target: EventTarget | null) =>
  popupPressedJustNow() || (target instanceof Element && target.closest('[data-toast-host]') !== null);

export function DialogContent({
  children,
  title,
  description,
  className,
  side,
  hideCloseButton,
  onPointerDownOutside,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      {/* charcoal-tinted scrim rather than neutral black — the dimmed app
          behind it stays part of the same palette instead of going grey. */}
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink-950/50 backdrop-blur-[6px]" />
      <DialogPrimitive.Content
        onPointerDownOutside={(e) => {
          // Closing an error popup is not a click "outside" the dialog.
          if (isInToastHost(e.target)) { e.preventDefault(); return; }
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => { if (isInToastHost(e.target)) e.preventDefault(); }}
        className={cn(
          'fixed z-50 text-fg-primary shadow-glass-lg outline-none',
          // Much denser than a Card, and deliberately so. A dialog's own
          // backdrop-filter samples the *dimmed* scrim behind it, so a light
          // tint here composites to muddy grey rather than frosted white. At
          // 90% over a 50% scrim the pane stays crisp and the text stays sharp
          // — this is a till, and the modal holds the primary action.
          'bg-white/90 backdrop-blur-2xl backdrop-saturate-150',
          'border border-white/60',
          side === 'right'
            ? 'right-0 top-0 h-full w-[480px] max-w-full border-l'
            : 'left-1/2 top-1/2 w-[480px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-lg',
          className,
        )}
      >
        {(title || !hideCloseButton) && (
          <div className="flex items-start justify-between border-b border-stroke-hairline/10 px-5 py-4">
            <div>
              {title && (
                <DialogPrimitive.Title className="text-heading text-fg-primary">{title}</DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="mt-0.5 text-body text-fg-secondary">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            {!hideCloseButton && (
              <DialogPrimitive.Close className="text-fg-tertiary hover:text-fg-primary transition-colors duration-120 ease-apple">
                <X size={18} />
              </DialogPrimitive.Close>
            )}
          </div>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
